import { ChangeDetectorRef, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { ContentTypesEnum } from '@enums/content-type.enum';
import { ExtrasTypesEnum } from '@enums/extras-types';
import { LoungeContent } from '@shared/services/content/_interfaces_/content.interface';
import { LoungeContentMapped } from '@shared/services/content/_interfaces_/content.mapped.interface';
import { ContentMapperService } from '@shared/services/content/content-mapper.service';
import { ContentService } from '@shared/services/content/content.service';
import { ExtrasInfo, FlightLeg, Passenger } from '@shared/services/extras/_interfaces_/extras-info.interface';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, finalize, map, take, takeUntil } from 'rxjs/operators';
import { ExtraProductsEnum } from '@enums/extra-products.enum';
import { DateFormatEnum } from '@enums/date-format.enum';
import { ExtrasOverviewService } from '@shared/services/extras/extras-overview.service';
import { ExtrasFormEnum } from '@enums/extras-form.enum';

@Component({
  selector: 'app-lounge-accordion',
  templateUrl: './lounge-accordion.component.html',
  styleUrls: ['./lounge-accordion.component.scss'],
})
export class LoungeAccordionComponent {
  private contentService = inject(ContentService);
  private contentMapperService = inject(ContentMapperService);
  private cd = inject(ChangeDetectorRef);
  private extrasOverviewService = inject(ExtrasOverviewService);

  @Input() extrasInfo: ExtrasInfo;
  @Input() culture: string;
  @Input() expandExtras: string;
  @Input() extrasTypesEnum = ExtrasTypesEnum;
  @Output() loungeSelectionChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  isLoungeAvailable: boolean = false;
  isSubAccordionOpen: boolean = false;
  passengers: Passenger[];
  flightLegs: FlightLeg[];
  loungeForm: FormGroup;
  extraProductsEnum = ExtraProductsEnum;
  ddMMyyyy: string = DateFormatEnum.ddMMyyyy;
  loading: boolean = false;
  removing: boolean = false;
  loadingIndex: number | null = null;
  removingIndex: number | null = null;
  flightIdentifier: string = '';
  private unsubscribeAll$: Subject<void> = new Subject();

  ngOnInit(): void {
    this.isLoungeAvailable = this.checkLoungeAvailability();
    this.flightIdentifier = this.extrasInfo?.flightIdentifier;
    this.passengers = this.extrasInfo?.passengers;
    this.flightLegs = this.extrasInfo?.flights[0]?.flightLegs;
    this.initForm();
  }

  readonly contentLounge$: Observable<LoungeContentMapped> = this.contentService
    .getContent<LoungeContent>(ContentTypesEnum.myFfExtrasPage)
    .pipe(map((content: LoungeContent) => this.contentMapperService.mapLoungeContent(content)));

  initForm(): void {
    const passangersArrayForm = new FormArray([]);
    if (this.extrasInfo?.passengers?.length > 0) {
      this.extrasInfo.passengers.forEach((passenger) => {
        passangersArrayForm.push(
          new FormGroup({
            isAccordionOpen: new FormControl(false),
            travellerIdentifier: new FormControl(passenger.passengerIdentifier ?? ''),
            loungeOptionCode: new FormControl(passenger.passengerExtras[ExtraProductsEnum.lounge]?.options[0]?.bookingReference ?? ''),
          })
        );
      });
    }
    this.loungeForm = new FormGroup({
      passengersLounge: passangersArrayForm,
    });
  }

  removeLounge(passengerIdentifier: string, passengerIndex: number, optionIndex: number, remove: boolean= false): void {
    if (this.removing) {
      return;
    }

    this.removing = true;
    this.removingIndex = passengerIndex;

    const loungeOptionCode = "";
    let { travellerIdentifier } = this.loungeForm.value.passengersLounge.find(
      (passenger) => passenger[ExtrasFormEnum.TravellerIdentifier] === passengerIdentifier
    );

    this.extrasOverviewService
      .putUpdateLounge(this.flightIdentifier, travellerIdentifier, loungeOptionCode)
      .pipe(
        take(1),
        takeUntil(this.unsubscribeAll$),
        catchError(() => {
          return EMPTY;
        }),
        finalize(() => {
          this.removing = false;
          this.removingIndex = null;
          this.cd.markForCheck();
        })
      )
      .subscribe((data) => {
        this.extrasInfo = data;
        this.extrasOverviewService.setExtrasData(data);
        this.isAnyLoungeSelected();
        this.cd.markForCheck();
      });
  }

  selectLounge(passengerIdentifier: string, passengerIndex: number, optionIndex: number, remove: boolean= false): void {
    if (this.loading) {
      return;
    }

    this.loading = true;
    this.loadingIndex = passengerIndex;

    let { loungeOptionCode, travellerIdentifier } = this.loungeForm.value.passengersLounge.find(
      (passenger) => passenger[ExtrasFormEnum.TravellerIdentifier] === passengerIdentifier
    );

    if (remove) {
      loungeOptionCode = "";
    }

    this.extrasOverviewService
      .putUpdateLounge(this.flightIdentifier, travellerIdentifier, loungeOptionCode)
      .pipe(
        take(1),
        takeUntil(this.unsubscribeAll$),
        catchError(() => {
          return EMPTY;
        }),
        finalize(() => {
          this.loading = false;
          this.loadingIndex = null;
          this.cd.markForCheck();
        })
      )
      .subscribe((data) => {
        this.extrasInfo = data;
        this.extrasOverviewService.setExtrasData(data);
        this.isAnyLoungeSelected();
        this.cd.markForCheck();
      });
  }

  checkLoungeAvailability(): boolean {
    if (this.extrasInfo) {
      for (let passenger of this.extrasInfo.passengers) {
        if (passenger?.passengerExtras?.filter((extras) => extras.type === ExtrasTypesEnum.LoungeAccess)[1]?.options?.length > 0) {
          return true;
        }
      }
      return false;
    }
  }

  setAccordionOpen(optionIndex: number): void {
    let isAccordionOpen = this.loungeForm?.value?.passengersLounge[optionIndex]?.IsAccordionOpen;
    this.isSubAccordionOpen = !isAccordionOpen;
  }

  getAccordionOpen(optionIndex: number): boolean {
    return this.loungeForm?.value?.passengersLounge[optionIndex]?.IsAccordionOpen;
  }

  get hasLoungeSelected(): boolean {
    return this.extrasInfo?.passengers
      .flatMap(passenger => passenger.passengerExtras)
      .filter(extra => extra.type === ExtrasTypesEnum.LoungeAccess)
      .some(extra => extra.options.some(option => option.isSelected));
  }

  isLoungeSelected(passengerIndex: number, optionIndex: number): boolean {
    return this.extrasInfo?.passengers[passengerIndex]?.passengerExtras[ExtraProductsEnum.lounge]?.options[optionIndex]?.isSelected;
  }

  isAnyLoungeSelected(): boolean {
    if(this.extrasInfo) {
      const loungeSelected = this.extrasInfo?.passengers?.some((passenger) =>
        passenger.passengerExtras[ExtraProductsEnum.lounge]?.options?.some((option) => option.isSelected && option.pieces !== 0)
      );
      this.loungeSelectionChanged.emit(loungeSelected);
      return loungeSelected;
    }
    return false;
  }
}
